import type { ReactNode } from "react";
import { Flex, Tag, Typography, theme } from "antd";
import { ArrowRightOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import styles from "styles/layout.scss";
import type { SearchEntry } from "./types";

const { Text } = Typography;
const { useToken } = theme;

// Bolds the first case-insensitive occurrence of the query inside the label.
// Diacritic-normalized matches whose indices shifted simply render un-highlighted
// rather than risk marking the wrong characters.
const highlightedLabel = (label: string, query: string): ReactNode => {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return label;

  const matchIndex = label.toLowerCase().indexOf(trimmedQuery.toLowerCase());
  if (matchIndex === -1) return label;

  return (
    <>
      {label.slice(0, matchIndex)}
      <Text strong>{label.slice(matchIndex, matchIndex + trimmedQuery.length)}</Text>
      {label.slice(matchIndex + trimmedQuery.length)}
    </>
  );
};

interface AppSearchOptionProps {
  entry: SearchEntry;
  query?: string;
}

// Presentational row for a single search result: icon, label + group, and a kind tag.
const AppSearchOption = ({ entry, query = "" }: AppSearchOptionProps) => {
  const { t } = useTranslation();
  const { token } = useToken();
  // The dropdown already shows the top-level module as a group header, so the
  // per-row breadcrumb is only useful when it carries a deeper path.
  const showGroup = Boolean(entry.group) && entry.group !== entry.topGroup;

  return (
    <Flex align="center" gap={10} className={styles.q_app_search__option}>
      <span className={styles.q_app_search__icon} style={{ color: token.colorPrimary }}>
        {entry.icon}
      </span>
      <Flex vertical className={styles.q_app_search__copy}>
        <span className={styles.q_app_search__label}>{highlightedLabel(entry.label, query)}</span>
        {showGroup && (
          <Text type="secondary" ellipsis className={styles.q_app_search__group}>
            {entry.group}
          </Text>
        )}
      </Flex>
      <Tag
        color={entry.kind === "create" ? "green" : "default"}
        icon={entry.kind === "create" ? <PlusOutlined /> : <ArrowRightOutlined />}
        className={styles.q_app_search__tag}
      >
        {t(`frontend.app_search.${entry.kind}`)}
      </Tag>
    </Flex>
  );
};

export default AppSearchOption;
